import type { GameEvent } from '@/content/schema';
import type { Ctx } from '@/core/context';
import { bus } from '@/core/bus';
import { nextRandom, shuffle } from '@/core/rng';
import { clamp } from '@/core/state';
import { distToRect } from '@/world/collision';
import { grantItem, hasItem } from './items';
import { bump } from './achievements';
import { tr } from '@/i18n';

/**
 * 穿插型事件（食品工廠-3）：
 * 走到定點／操作機台／疲勞過高／隨機 → 觸發短事件 → A（安全，花時間）或 B（省時間，有風險）
 * B 的事故機率 = 基礎風險 + 疲勞 + 壓力 + 先前忽略警訊次數 − 已學到的經驗 − 綠色乖乖（心理安定）
 * 受傷或死亡後可以說「我要重新活一次」回到事件發生前，重新選擇。
 */
const TIER_COLOR = { A: '#8fa4ae', B: '#e3b341', C: '#e07b39', D: '#d9534a' } as const;
const TIER_NAME = { A: tr("環境提醒"), B: tr("輕微異常"), C: tr("工傷事件"), D: tr("重大事故") } as const;

export function eligible(ctx: Ctx, ev: GameEvent): boolean {
  const { day } = ctx;
  if (ev.oncePerDay && day.events.firedToday.includes(ev.id)) return false;
  const last = day.events.last[ev.id];
  if (last != null && day.clock - last < ev.cooldownMinutes) return false;
  if (day.player.fatigue < ev.trigger.minFatigue) return false;
  return true;
}

/**
 * 隨機事件節奏：大約每 10～15 分鐘（實際遊玩秒數）才允許一次隨機事件。
 * 例外：回溯後重開的事件、單向門提醒、綠色乖乖被發現、玩家自己去翻的紙箱。
 */
export const randomReady = (ctx: Ctx) => ctx.day.events.playSec >= ctx.day.events.nextRandomAt;
export function scheduleNext(ctx: Ctx, first = false): void {
  const [a, b] = ctx.c.game.events.randomIntervalSec;
  const gap = a + nextRandom(ctx.day) * (b - a);
  ctx.day.events.nextRandomAt = ctx.day.events.playSec + (first ? gap * 0.5 : gap);
}

/** 每 0.4 秒呼叫一次：檢查玩家是否走進事件觸發範圍 */
export function tickProximity(ctx: Ctx): void {
  if (ctx.paused || ctx.ui.isOpen()) return;
  const hairCheck = ctx.day.flags.hairOut;
  const rt = ctx.world.rt(); const [x, z] = ctx.world.playerPos(); const { day, c } = ctx;
  const nowInside: string[] = [];
  for (const ev of c.events) {
    if (ev.trigger.type !== 'proximity') continue;
    for (const a of ev.trigger.anchors) {
      if (a.floor !== day.floor) continue;
      const an = rt.anchor(a.target); if (!an) continue;
      if (distToRect(x, z, an.box) <= ev.trigger.radius) {
        const key = `${ev.id}@${a.target}`; nowInside.push(key);
        if (day.events.inside.includes(key)) continue;           // 每次「進入」只擲一次
        if (!eligible(ctx, ev)) continue;
        const special = hairCheck && ev.id === 'INT-014';
        if (!special && !randomReady(ctx)) continue;
        // 綠色乖乖放在本樓層 → 機率略降；回溯過（已學到）的事件一定再次出現，讓玩家改變選擇
        let chance = c.game.risk.proximityTriggerChance;
        if (day.items.guaiguai?.floor === day.floor) chance -= 0.1;
        if (ctx.profile.eventsLearned.includes(ev.id) || special) chance = 1;
        if (ev.tier === 'D' && day.flags[`primed:${ev.tags?.[0]}`]) chance += 0.2;
        if (nextRandom(day) < chance) { day.events.inside = [...nowInside]; fire(ctx, ev); return; }
      }
    }
  }
  day.events.inside = nowInside;
}

export function tickTimed(ctx: Ctx): void {
  if (ctx.paused || ctx.ui.isOpen() || !randomReady(ctx)) return;
  const { day, c } = ctx;
  const fat = c.eventById.get('SHORT-005');
  if (fat && day.player.fatigue >= 70 && eligible(ctx, fat)) { fire(ctx, fat); return; }
  const rnd = c.eventById.get('SHORT-010');
  if (rnd && ['1F', '2F', '3F', 'B1'].includes(day.floor) && eligible(ctx, rnd) && nextRandom(day) < 0.02) fire(ctx, rnd);
}

/** 檢查目標機台前的提醒（SHORT-002／SHORT-007）。回傳 true 表示已攔截並顯示事件 */
export function maybeBeforeInspect(ctx: Ctx, then: () => void): boolean {
  const pool = ctx.c.events.filter(e => e.trigger.type === 'beforeInspect' && eligible(ctx, e));
  if (!pool.length || !randomReady(ctx) || nextRandom(ctx.day) > 0.6) return false;
  fire(ctx, pool[Math.floor(nextRandom(ctx.day) * pool.length)], then);
  return true;
}

export function riskOf(ctx: Ctx, ev: GameEvent, base: number): number {
  const { day, c } = ctx, R = c.game.risk;
  let p = base + day.player.fatigue * R.fatigueWeight + day.player.stress * R.stressWeight + day.events.ignored * R.ignoredWarningWeight;
  if (ctx.profile.eventsLearned.includes(ev.id)) p += R.knowledgeBonus;
  if (day.items.guaiguai?.floor === day.floor) p -= 0.05;
  return clamp(p, 0.05, 0.95);
}

export function fire(ctx: Ctx, ev: GameEvent, then?: (safe: boolean) => void, opts: { ignoreGate?: boolean } = {}): void {
  const { day } = ctx;
  const gated = !opts.ignoreGate && !['SHORT-006', 'SHORT-004', 'SHORT-009'].includes(ev.id);
  if (gated) scheduleNext(ctx);
  if (ev.id === 'INT-014') day.flags.hairOut = false;
  day.events.last[ev.id] = day.clock;
  if (!day.events.firedToday.includes(ev.id)) day.events.firedToday.push(ev.id);
  if (ev.tier === 'C' || ev.tier === 'D') ctx.checkpoint(ev.rewindPoint, ev.id);
  for (const h of ev.hazards) if (!ctx.profile.hazardsSeen.includes(h)) ctx.profile.hazardsSeen.push(h);
  if (ev.primes) day.flags[`primed:${ev.primes}`] = true;
  ctx.save();
  showEvent(ctx, ev, then);
}

export function showEvent(ctx: Ctx, ev: GameEvent, then?: (safe: boolean) => void): void {
  bus.emit('sfx', ev.tier === 'D' ? 'alarm' : 'warn');
  const learned = ctx.profile.eventsLearned.includes(ev.id);
  const warn = hasItem(ctx, 'charm-senior');
  const order = shuffle([0, 1], { rng: ev.id.length * 977 + ctx.day.day });
  ctx.ui.card({
    name: ev.title, role: [TIER_NAME[ev.tier], ev.locationText].filter(Boolean).join(tr("・")), color: TIER_COLOR[ev.tier],
    badge: { text: tr("{0}｜{1} 級", ev.id, ev.tier), color: TIER_COLOR[ev.tier] },
    text: ev.text,
    sections: [
      ...(ev.npcLine ? [{ label: tr("同事"), items: [ev.npcLine] }] : []),
      ...(learned ? [{ label: tr("你記得"), items: [tr("上一輪就是在這裡出事的。這次你知道會發生什麼。")], tone: 'warn' as const }] : []),
    ],
    choices: order.map(i => {
      const o = ev.options[i];
      let note: string | undefined;
      if (!o.safe && o.risk && (warn || learned)) {
        const p = riskOf(ctx, ev, o.risk.base);
        note = tr("{0}風險 {1}{2}", warn ? tr("🪬 前輩護身符：") : '', p >= 0.6 ? tr("很高") : p >= 0.35 ? tr("偏高") : tr("中等"), o.risk.injury ? tr("（{0}）", o.risk.injury) : '');
      }
      return { text: o.text, note, fn: () => resolve(ctx, ev, i, then) };
    }),
  });
}

function resolve(ctx: Ctx, ev: GameEvent, i: number, then?: (safe: boolean) => void): void {
  const { day } = ctx;
  const o = ev.options[i];
  const done = () => { ctx.ui.close(); then?.(o.safe); };
  if (o.grants) grantItem(ctx, o.grants, true);
  if (o.safe) {
    ctx.advance(o.minutes, ev.title);
    ctx.apply(o.effects, ev.id);
    ctx.log(tr("{0} {1}：{2}", ev.id, ev.title, o.text), 'good');
    bus.emit('sfx', 'ok');
    ctx.ui.card({ name: ev.title, role: tr("處置結果"), color: '#3f9e6c', text: o.result || tr("風險解除。"),
      sections: ev.notes?.length ? [{ label: tr("補充"), items: ev.notes }] : undefined,
      choices: [{ text: o.grants ? tr("收下，繼續") : tr("繼續"), tone: 'primary', fn: done }] });
    ctx.save(); return;
  }
  // 冒險選項
  day.events.ignored++;
  ctx.advance(o.minutes, ev.title);
  const risk = o.risk;
  const p = risk ? riskOf(ctx, ev, risk.base) : 0;
  const hit = risk ? nextRandom(day) < p : false;
  ctx.log(tr("{0} {1}：{2}{3}", ev.id, ev.title, o.text, hit ? tr("（出事了）") : tr("（這次沒事）")), hit ? 'bad' : 'warn');
  if (!hit) {
    ctx.ui.card({ name: ev.title, role: tr("這次沒事"), color: '#e3b341', text: tr("……這次運氣好，什麼都沒發生。但風險還在那裡。{0}", o.result ? tr("\n（可能的後果：{0}）", o.result) : ''),
      choices: [{ text: tr("繼續"), fn: done }] });
    ctx.save(); return;
  }
  ctx.apply(o.effects, ev.id);
  if (!risk || risk.severity === 'none') {
    bus.emit('sfx', 'warn');
    ctx.ui.card({ name: ev.title, role: tr("後果"), color: '#e3b341', text: o.result || tr("產生了衛生或追溯上的缺失。"),
      sections: ev.notes?.length ? [{ label: tr("補充"), items: ev.notes }] : undefined, choices: [{ text: tr("繼續"), fn: done }] });
    ctx.save(); return;
  }
  // 受傷／死亡
  let severity: 'light' | 'major' | 'fatal' = risk.severity === 'light' ? 'light' : 'major';
  if (severity === 'major' && nextRandom(day) < ctx.c.game.risk.majorFatalChance) severity = 'fatal';
  accident(ctx, ev, severity, risk.injury ?? tr("受傷"), then);
}

/** 事故畫面：可以「我要重新活一次」 */
export function accident(ctx: Ctx, ev: GameEvent, severity: 'light' | 'major' | 'fatal', label: string, then?: (safe: boolean) => void): void {
  const { day, c } = ctx;
  bus.emit('sfx', 'alarm');
  if (!ctx.profile.eventsLearned.includes(ev.id)) ctx.profile.eventsLearned.push(ev.id);
  bump(ctx, 'accidents');
  const hpLoss = severity === 'light' ? 25 : severity === 'major' ? 70 : 100;
  day.player.hp = clamp(day.player.hp - hpLoss);
  day.player.stress = clamp(day.player.stress + 15);
  const injury = { label, severity, at: day.clock, source: ev.id };
  day.injuries.push(injury);
  ctx.save();
  const title = severity === 'fatal' ? tr("你沒能平安下班。") : severity === 'major' ? tr("重傷，需要送醫。") : tr("你受傷了。");
  const free = day.items.held.includes('charm-today') && !day.items.freeRewindUsed;
  const choices: Parameters<Ctx['ui']['card']>[0]['choices'] = [
    { text: tr("「{0}」{1}", c.game.rewind.phrase, free ? tr("（🧧 平安符：這次不扣專注）") : ''), tone: 'primary', fn: () => ctx.rewind() },
  ];
  if (severity === 'light') {
    choices.push({ text: tr("先依程序處置、通報，帶傷繼續工作"), tone: 'muted', fn: () => {
      if (nextRandom(day) < c.game.risk.lightStopWorkChance) { ctx.advance(30, tr("停工處理")); ctx.ui.toast(tr("傷勢需要停工處理 30 分鐘")); }
      else ctx.advance(10, tr("處置傷口"));
      ctx.log(tr("受傷：{0}（已通報）", label), 'bad'); ctx.ui.close(); then?.(false);
    } });
  } else {
    choices.push({ text: severity === 'fatal' ? tr("接受結果（本班結束）") : tr("送醫（本班結束）"), tone: 'danger', fn: () => {
      ctx.log(tr("{0}：{1}", severity === 'fatal' ? tr("死亡") : tr("重傷"), label), 'bad'); ctx.ui.close(); ctx.ui.ending();
    } });
  }
  ctx.ui.card({ name: title, role: tr("{0}｜{1}", ev.id, ev.title), color: '#d9534a', badge: { text: severity === 'fatal' ? tr("死亡") : severity === 'major' ? tr("重傷") : tr("工傷"), color: '#d9534a' },
    text: tr("{0}。\n回溯點：{1}", label, ev.rewindPoint),
    sections: [
      ...(ev.notes?.length ? [{ label: tr("真正的原因"), items: ev.notes, tone: 'bad' as const }] : []),
      { label: tr("正確作法"), items: [ev.options[0].text], tone: 'good' },
    ],
    choices });
}
